import { useEffect, useState } from 'react'
import { Loading, Modal } from 'react-daisyui'
import { IconDownload, IconShare } from '@tabler/icons-react'
import { Button } from '@/components/Button.tsx'
import { FileSelectionType, SelectionMode } from '@/hooks/useFileSelection.tsx'
import { Album } from '@/models/album.ts'
import { HeyDrive } from '@/models/drive.ts'
import {
  CreateShareRequest,
  MultipleDownloadCreateResponse,
  MultipleDownloadStatusResponse,
  ShareAlbumSelection,
  useClient,
} from '@/stores/useClient.ts'
import ShareableInput from '@/components/ShareableInput.tsx'
import DownloadQualityOptions from '@/components/Files/DownloadQualityOptions.tsx'
import DownloadProgressBar from '@/components/Files/DownloadProgressBar.tsx'
import { DownloadQuality, resolveDriveDownloadQualities } from '@/models/downloadOptions.ts'
import useDownloadProgress from '@/hooks/useDownloadProgress.ts'
import { getApiErrorStatus } from '@/controller/apiErrors.ts'

interface PreSelectionModalProps {
  mode: 'download' | 'share'
  modalRef: React.RefObject<HTMLDialogElement>
  drive: HeyDrive
  albums: Album[]
  fileSelection: FileSelectionType
}

type Step = 'scope' | 'albums' | 'info-individual' | 'quality' | 'processing' | 'share-done'

function scrollToContentIfNeeded() {
  const contentEl = document.getElementById('album-content')
  if (contentEl) {
    const rect = contentEl.getBoundingClientRect()
    if (rect.top > window.innerHeight * 0.8) {
      contentEl.scrollIntoView({ behavior: 'smooth' })
    }
  }
}

export default function PreSelectionModal({
  mode,
  modalRef,
  drive,
  albums,
  fileSelection,
}: PreSelectionModalProps) {
  const [step, setStep] = useState<Step>('scope')
  const [selectedAlbumIds, setSelectedAlbumIds] = useState<string[]>([])
  const [, setIsProcessing] = useState(false)
  const [shareLink, setShareLink] = useState<string | null>(null)
  const { requestMultiDownload, getMultiDownload, createShare } = useClient()
  const { progress, reportProgress, resetProgress } = useDownloadProgress()

  const isDownload = mode === 'download'
  const Icon = isDownload ? IconDownload : IconShare
  // Which resolutions this drive offers (watermark wins over the setting).
  const downloadQualities = resolveDriveDownloadQualities(drive)

  // Reset step when mode changes (same modal reused for download/share)
  useEffect(() => {
    resetState()
  }, [mode])

  function resetState() {
    setStep('scope')
    setSelectedAlbumIds([])
    setIsProcessing(false)
    setShareLink(null)
    resetProgress()
  }

  function closeModal() {
    resetState()
    modalRef.current?.close()
  }

  function getTitle(): string {
    switch (step) {
      case 'scope': return isDownload ? 'Was möchtest du herunterladen?' : 'Was möchtest du teilen?'
      case 'albums': return 'Alben auswählen'
      case 'info-individual': return isDownload ? 'Bilder auswählen' : 'Bilder auswählen'
      case 'quality': return 'Qualität wählen'
      case 'processing': return isDownload ? 'Download' : 'Teilen'
      case 'share-done': return 'Teilen-Link erstellt'
    }
  }

  // --- Download helpers ---

  /**
   * Enter the quality step — or skip it when the drive leaves only one resolution:
   * a step with a single button is not a decision.
   */
  function goToQuality(albumIds: string[]) {
    if (downloadQualities.length === 1) {
      startDownload(albumIds, downloadQualities[0])
      return
    }
    setStep('quality')
  }

  async function startDownload(albumIds: string[], quality: DownloadQuality) {
    setStep('processing')
    setIsProcessing(true)
    resetProgress()
    try {
      const res = await requestMultiDownload(drive.id, albumIds, [], quality)
      await pollDownload(res.data)
    } catch (e: any) {
      setIsProcessing(false)
      // No login needed for downloads; a 403 means the content is private.
      alert(getApiErrorStatus(e) === 403
        ? 'Diese Inhalte sind privat und können nicht heruntergeladen werden.'
        : 'Fehler beim Download')
      closeModal()
    }
  }

  async function pollDownload(createData: MultipleDownloadCreateResponse) {
    const intervalId = setInterval(async () => {
      try {
        const res = await getMultiDownload(drive.id, createData.id)
        const data: MultipleDownloadStatusResponse = res.data

        if (data.status !== 'failed') {
          reportProgress(data.progress)
        }

        if (data.status === 'done') {
          clearInterval(intervalId)
          setIsProcessing(false)
          const link = document.createElement('a')
          link.href = data.download_url!
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          closeModal()
        } else if (data.status === 'failed') {
          clearInterval(intervalId)
          setIsProcessing(false)
          alert('Download fehlgeschlagen')
          closeModal()
        }
      } catch {
        clearInterval(intervalId)
        setIsProcessing(false)
        alert('Download fehlgeschlagen')
        closeModal()
      }
    }, 1000)
  }

  // --- Share helpers ---

  async function startShare(albumIds: string[], includeAll: boolean) {
    setStep('processing')
    setIsProcessing(true)

    const shareAlbums: ShareAlbumSelection[] = albumIds.map(id => ({
      album_id: id,
      include_all_files: includeAll,
    }))

    const request: CreateShareRequest = { albums: shareAlbums }

    try {
      const response = await createShare(drive.id, request)
      const shareUrl = `${window.location.origin}/s/${response.data.id}`
      setShareLink(shareUrl)
      setStep('share-done')
    } catch (e: any) {
      // No login needed for sharing; a 403 means the content is private.
      alert(getApiErrorStatus(e) === 403
        ? 'Diese Inhalte sind privat und können nicht geteilt werden.'
        : 'Fehler beim Erstellen des Teilen-Links')
      closeModal()
    } finally {
      setIsProcessing(false)
    }
  }

  // --- Scope handlers ---

  function handleFullGallery() {
    const allAlbumIds = albums.map(a => a.id)
    if (isDownload) {
      setSelectedAlbumIds(allAlbumIds)
      goToQuality(allAlbumIds)
    } else {
      startShare(allAlbumIds, true)
    }
  }

  function handleSpecificAlbums() {
    setSelectedAlbumIds([])
    setStep('albums')
  }

  function handleIndividualFiles() {
    setStep('info-individual')
  }

  function handleStartIndividualSelection() {
    closeModal()
    // Cancel existing selection if active
    if (fileSelection.isSelecting) {
      fileSelection.setIsSelecting(false)
    }
    setTimeout(() => {
      fileSelection.setSelectionMode(isDownload ? SelectionMode.DOWNLOAD : SelectionMode.SHARE)
      fileSelection.setIsSelecting(true)
      scrollToContentIfNeeded()
    }, 0)
  }

  function toggleAlbum(albumId: string) {
    setSelectedAlbumIds(prev =>
      prev.includes(albumId)
        ? prev.filter(id => id !== albumId)
        : [...prev, albumId]
    )
  }

  function handleAlbumsConfirm() {
    if (selectedAlbumIds.length === 0) return
    if (isDownload) {
      goToQuality(selectedAlbumIds)
    } else {
      startShare(selectedAlbumIds, true)
    }
  }

  function handleQualitySelect(quality: DownloadQuality) {
    startDownload(selectedAlbumIds, quality)
  }

  return (
    <Modal ref={modalRef} className='bg-neutral'>
      <Modal.Header>
        <div className='flex flex-row gap-2 justify-center'>
          <div className='bg-primary text-neutral rounded-full p-1'>
            <Icon size={20} />
          </div>
          {getTitle()}
        </div>
        <form method='dialog'>
          <button className='btn btn-sm btn-circle btn-ghost absolute right-5 top-5' onClick={resetState}>
            ✕
          </button>
        </form>
      </Modal.Header>
      <Modal.Body>
        {/* Step: Scope selection */}
        {step === 'scope' && (
          <div className='flex flex-col gap-3 items-center'>
            <Button color='primary' className='w-full' onClick={handleFullGallery}>
              Ganze Galerie
            </Button>
            {albums.length > 1 && (
              <Button color='primary' variant='outline' className='w-full' onClick={handleSpecificAlbums}>
                Bestimmte Alben
              </Button>
            )}
            <Button color='primary' variant='outline' className='w-full' onClick={handleIndividualFiles}>
              Einzelne Bilder
            </Button>
          </div>
        )}

        {/* Step: Info for individual file selection */}
        {step === 'info-individual' && (
          <div className='flex flex-col gap-4 items-center'>
            <p className='text-center'>
              Du wirst jetzt in den Auswahl-Modus versetzt. Wähle die gewünschten Bilder aus und drücke dann auf den {isDownload ? 'Download' : 'Teilen'}-Button.
            </p>
            <Button color='primary' onClick={handleStartIndividualSelection}>
              Bilder auswählen
            </Button>
            <Button color='ghost' onClick={() => setStep('scope')}>
              Zurück
            </Button>
          </div>
        )}

        {/* Step: Album selection */}
        {step === 'albums' && (
          <div className='flex flex-col gap-3'>
            {albums.map(album => (
              <label key={album.id} className='flex items-center gap-3 cursor-pointer px-2 py-2 rounded-lg hover:bg-base-200'>
                <input
                  type='checkbox'
                  className='checkbox checkbox-primary'
                  checked={selectedAlbumIds.includes(album.id)}
                  onChange={() => toggleAlbum(album.id)}
                />
                <span className='flex-1 truncate'>{album.title}</span>
              </label>
            ))}
            <Button
              color='primary'
              className='mt-2'
              disabled={selectedAlbumIds.length === 0}
              onClick={handleAlbumsConfirm}
            >
              {isDownload ? 'Weiter' : 'Teilen-Link erstellen'}
            </Button>
            <Button color='ghost' onClick={() => setStep('scope')}>
              Zurück
            </Button>
          </div>
        )}

        {/* Step: Quality selection (download only) */}
        {step === 'quality' && (
          <div className='flex flex-col gap-3 items-center'>
            <DownloadQualityOptions
              qualities={downloadQualities}
              onSelect={handleQualitySelect}
              fullWidth
            />
            <Button color='ghost' className='mt-2' onClick={() => setStep(selectedAlbumIds.length === albums.length ? 'scope' : 'albums')}>
              Zurück
            </Button>
          </div>
        )}

        {/* Step: Processing */}
        {step === 'processing' && (
          <div className='flex flex-col gap-3 items-center py-4'>
            {isDownload ? (
              <DownloadProgressBar progress={progress} />
            ) : (
              <>
                <Loading color='primary' />
                <p className='text-center'>Teilen-Link wird erstellt...</p>
              </>
            )}
          </div>
        )}

        {/* Step: Share done */}
        {step === 'share-done' && shareLink && (
          <div className='flex flex-col gap-4 items-center'>
            <p className='text-center opacity-80'>
              Teile diesen Link, um anderen Zugriff auf die ausgewählten Inhalte zu geben.
            </p>
            <div className='w-full'>
              <ShareableInput value={shareLink} />
            </div>
            <Button color='primary' variant='outline' className='mt-2' onClick={closeModal}>
              Schließen
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}
